import { ToastType } from '@components/Toast';
import { handleToast } from '@utils/utils';
import { produce } from 'solid-js/store';

import {
  addCollectionCommentCall,
  applyCollectionActionsCall,
  deleteCollectionCommentCall,
  editCollectionCommentCall,
  fetchATGLookupValuesCall,
  fetchCollectionsTierCall,
  getCollectionCommentsCall,
  getCollectionsByCustomer,
  sendToCollectionsCall,
  updateCollectionsKeyReasonCall,
  updateCollectionsRiskPercentCall,
  updateCollectionsTierCall,
  updateNextActionCall,
} from './services';
import { collectionDetailsStore, setCollectionDetailsStore } from './store';
import {
  ActionNote,
  AddCollectionCommentPayload,
  CollectionActionPayload,
  CollectionDetails,
  EditCollectionCommentPayload,
  IComment,
} from './types';

export const fetchCollectionsByCustomer = async (customerId: number) => {
  setCollectionDetailsStore('isCollectionLoading', true);
  const resp = await getCollectionsByCustomer(customerId);
  if (resp && resp.isSuccess) {
    setCollectionDetailsStore(
      produce((draft) => {
        draft.collectionDetails = resp.value;
        draft.isCollectionLoading = false;
      }),
    );
  } else {
    // handleToast(ToastType.Error, resp.e as string);
    setCollectionDetailsStore('isError', true);
  }
  setCollectionDetailsStore('isCollectionLoading', false);
};

export const updateCollectionDetailsState = (
  updates: Partial<CollectionDetails>,
) => {
  Object.entries(updates).forEach(([key, value]) => {
    setCollectionDetailsStore(
      'collectionDetails',
      key as keyof CollectionDetails,
      value,
    );
  });
};

export const updateActionNotesState = (updates: Partial<ActionNote>) => {
  Object.entries(updates).forEach(([key, value]) => {
    setCollectionDetailsStore('actionNotes', key as keyof ActionNote, value);
  });
};

export const updateCollectionsRiskPercent = async (payload: {
  ids: number[];
  collectionsRiskPercent: number | string;
}) => {
  setCollectionDetailsStore('isLoading', true);
  const resp = await updateCollectionsRiskPercentCall(payload);
  setCollectionDetailsStore('isLoading', false);
  return resp;
};

export const applyCollectionActions = async (
  payload: CollectionActionPayload[],
  cb?: () => void,
) => {
  setCollectionDetailsStore('isLoading', true);
  const res = await applyCollectionActionsCall(payload);
  if (res && res.isSuccess) {
    handleToast(ToastType.Success, 'Actions applied successfully');
    cb && cb();
  }
  setCollectionDetailsStore('isLoading', false);
};

export const fetchCollectionsTier = async () => {
  const data = await fetchCollectionsTierCall();
  if (data && data.isSuccess) {
    setCollectionDetailsStore('collectionTier', data.value);
  }
};

export const updateCollectionsTier = async (payload: {
  id: string;
  tierId: string;
}) => {
  setCollectionDetailsStore('isLoading', true);
  const res = await updateCollectionsTierCall(payload);
  if (res && res.isSuccess) {
    handleToast(ToastType.Success, 'Collections Tier successfully assigned!');
    updateCollectionDetailsState({
      customer: {
        ...collectionDetailsStore.collectionDetails.customer,
        collectionTierId: res.value.tierId,
      },
    });
  }
  setCollectionDetailsStore('isLoading', false);
};

export const fetchATGLookupValues = async () => {
  const data = await fetchATGLookupValuesCall();
  if (data) {
    setCollectionDetailsStore('aTGLookupValue', data);
  }
};

export const updateCollectionsKeyReason = async (payload: {
  id: number;
  keyReasonId: number;
}) => {
  setCollectionDetailsStore('isLoading', true);
  const res = await updateCollectionsKeyReasonCall(payload);
  if (res && res.isSuccess) {
    handleToast(ToastType.Success, 'Key Reason successfully assigned!');
    updateCollectionDetailsState({
      customer: {
        ...collectionDetailsStore.collectionDetails.customer,
        keyReasonId: Number(payload.keyReasonId),
      },
    });
  }
  setCollectionDetailsStore('isLoading', false);
};

export const updateNextAction = async (payload: {
  id: string;
  stringDate: string | null;
}) => {
  setCollectionDetailsStore('isLoading', true);
  const resp = await updateNextActionCall(payload);
  setCollectionDetailsStore('isLoading', false);
  return resp;
};

export const getCollectionComments = async (id: number) => {
  const resp = await getCollectionCommentsCall(id);
  if (resp && resp.isSuccess) {
    setCollectionDetailsStore('collectionComments', resp.value);
  }
};

export const addCollectionComment = async (
  payload: AddCollectionCommentPayload,
  callback: () => void,
) => {
  setCollectionDetailsStore('isLoading', true);
  const resp = await addCollectionCommentCall(payload);
  if (resp && resp.isSuccess) {
    const collectionComments = [...collectionDetailsStore.collectionComments];
    handleToast(ToastType.Success, 'Comment added successfully');
    collectionComments.unshift(resp.value as unknown as IComment);
    setCollectionDetailsStore('collectionComments', collectionComments);
    callback();
  }
  setCollectionDetailsStore('isLoading', false);
};

export const deleteCollectionComment = async (commentId: number) => {
  setCollectionDetailsStore('isLoading', true);
  const resp = await deleteCollectionCommentCall(commentId);
  const collectionComments = [
    ...collectionDetailsStore.collectionComments,
  ].filter((c) => c.id !== commentId);
  if (resp && resp.isSuccess) {
    setCollectionDetailsStore('collectionComments', collectionComments);
    handleToast(ToastType.Success, 'Comment deleted successfully');
  }
  setCollectionDetailsStore('isLoading', false);
};

export const sendToCollections = async (invoiceIds: number[]) => {
  setCollectionDetailsStore('isLoading', true);
  const resp = await sendToCollectionsCall(invoiceIds);
  if (resp && resp.isSuccess) {
    handleToast(ToastType.Success, 'Invoice sent to collections successfully');
  }
  setCollectionDetailsStore('isLoading', false);
};

export const editCollectionComment = async (
  payload: EditCollectionCommentPayload,
  callback: () => void,
) => {
  setCollectionDetailsStore('isLoading', true);
  const resp = await editCollectionCommentCall(payload);
  if (resp && resp.isSuccess) {
    const collectionComments = [
      ...collectionDetailsStore.collectionComments,
    ].map((c) =>
      c.id === payload.id ? { ...c, comment: payload.comment } : c,
    );
    handleToast(ToastType.Success, 'Comment updated successfully');
    setCollectionDetailsStore('collectionComments', collectionComments);
    callback();
  }
  setCollectionDetailsStore('isLoading', false);
};
